// Single shared navigation catalogue.
// Each item declares which modules it belongs to via `modules: [...]`.
// `getNavigationForModule(module)` returns a filtered tree for the active app.

import {
  AdjustmentsHorizontalIcon,
  BellAlertIcon,
  BuildingLibraryIcon,
  BuildingOffice2Icon,
  CalendarIcon,
  CircleStackIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  ChartBarSquareIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
  CloudIcon,
  DeviceTabletIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  HeartIcon,
  MapIcon,
  QuestionMarkCircleIcon,
  ShareIcon,
  SignalIcon,
  SunIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

import DashboardsIcon from 'assets/dualicons/dashboards.svg?react';
import {
  NAV_TYPE_ITEM,
} from 'constants/app.constant';
import { APP_MODULES } from 'constants/module.constant';

const ALL_MODULES = [
  APP_MODULES.INDUSTRIAL,
  APP_MODULES.MONITORING,
  APP_MODULES.MANAGER,
];

// --- Catalogue ---------------------------------------------------------

const dashboards = {
  id: 'dashboards',
  type: NAV_TYPE_ITEM,
  path: '/dashboard',
  title: 'Dashboards',
  transKey: 'nav.dashboards.dashboards',
  Icon: DashboardsIcon,
  modules: [APP_MODULES.INDUSTRIAL, APP_MODULES.MONITORING, APP_MODULES.MANAGER],
};

const climateAnalytics = {
  id: 'analytics',
  type: NAV_TYPE_ITEM,
  path: '/analytics',
  title: 'Climate',
  transKey: 'nav.dashboards.analytics',
  Icon: ChartBarSquareIcon,
  modules: [APP_MODULES.MONITORING],
};

const reports = {
  id: 'reports',
  type: NAV_TYPE_ITEM,
  path: '/reports',
  title: 'Analytics and Reporting',
  transKey: 'nav.dashboards.analyticsReports',
  Icon: ChartBarIcon,
  modules: [APP_MODULES.INDUSTRIAL, APP_MODULES.MONITORING, APP_MODULES.MANAGER],
  childs: [
    {
      id: 'reports.periodic_analysis',
      path: 'reports/periodic-analysis',
      type: NAV_TYPE_ITEM,
      title: 'Periodic Analysis',
      transKey: 'nav.dashboards.periodic_analysis',
      Icon: ChartBarIcon,
      modules: [APP_MODULES.MONITORING, APP_MODULES.MANAGER],
    },
    {
      id: 'reports.daily-profile',
      path: 'reports/daily-profile',
      type: NAV_TYPE_ITEM,
      title: 'Daily Profile',
      transKey: 'nav.dashboards.daily_profile',
      Icon: ChartBarIcon,
      modules: [APP_MODULES.MONITORING, APP_MODULES.MANAGER],
    },
    {
      id: 'reports.seasonal-analysis',
      path: 'reports/seasonal-analysis',
      type: NAV_TYPE_ITEM,
      title: 'Seasonal Analysis',
      transKey: 'nav.dashboards.seasonal_analysis',
      Icon: ChartBarIcon,
      modules: ALL_MODULES,
    },
    {
      id: 'reports.weather',
      path: 'reports/weather',
      type: NAV_TYPE_ITEM,
      title: 'Weather',
      transKey: 'nav.dashboards.weather',
      Icon: ChartBarIcon,
      modules: ALL_MODULES,
    },
    {
      id: 'reports.annual',
      path: 'reports/annual',
      type: NAV_TYPE_ITEM,
      title: 'Annual',
      transKey: 'nav.dashboards.annual',
      Icon: ChartBarIcon,
      modules: [APP_MODULES.MONITORING, APP_MODULES.MANAGER],
    },
    {
      id: 'reports.correlation_analysis',
      path: 'reports/correlation-analysis',
      type: NAV_TYPE_ITEM,
      title: 'Correlation Analysis',
      transKey: 'nav.dashboards.correlation_analysis',
      Icon: ChartBarIcon,
      modules: [APP_MODULES.MONITORING, APP_MODULES.MANAGER],
    }
  ],
};

const devices = {
  id: 'devices',
  type: NAV_TYPE_ITEM,
  path: '/device-management',
  title: 'Device Managements',
  transKey: 'nav.dashboards.deviceManagement',
  Icon: DeviceTabletIcon,
  modules: [APP_MODULES.INDUSTRIAL, APP_MODULES.MONITORING],
  childs: [
    {
      id: 'devices.overview',
      path: '/device-management/overview',
      type: NAV_TYPE_ITEM,
      title: 'Overview',
      transKey: 'nav.dashboards.overview',
      Icon: DeviceTabletIcon,
      modules: [APP_MODULES.INDUSTRIAL, APP_MODULES.MONITORING],
    },
    {
      id: 'devices.stations',
      path: '/device-management/stations',
      type: NAV_TYPE_ITEM,
      title: 'Stations',
      transKey: 'nav.dashboards.stations',
      Icon: DeviceTabletIcon,
      modules: [APP_MODULES.INDUSTRIAL, APP_MODULES.MONITORING],
    },
    {
      id: 'devices.calibration',
      path: '/device-management/calibration',
      type: NAV_TYPE_ITEM,
      title: 'Calibration',
      transKey: 'nav.dashboards.calibration',
      Icon: DeviceTabletIcon,
      modules: [APP_MODULES.INDUSTRIAL, APP_MODULES.MONITORING],
    },
    {
      id: 'devices.thresholds',
      path: '/device-management/thresholds',
      type: NAV_TYPE_ITEM,
      title: 'Thresholds',
      transKey: 'nav.dashboards.thresholds',
      Icon: AdjustmentsHorizontalIcon,
      modules: [APP_MODULES.INDUSTRIAL, APP_MODULES.MONITORING],
    },
    {
      id: 'devices.settings',
      path: '/device-management/settings',
      type: NAV_TYPE_ITEM,
      title: 'Settings',
      transKey: 'nav.dashboards.settings',
      Icon: DeviceTabletIcon,
      modules: [APP_MODULES.INDUSTRIAL, APP_MODULES.MONITORING],
    },
  ],
};

const alerts = {
  id: 'alerts',
  type: NAV_TYPE_ITEM,
  path: '/alerts',
  title: 'Alerts',
  transKey: 'nav.dashboards.alerts',
  Icon: BellAlertIcon,
  modules: [APP_MODULES.INDUSTRIAL, APP_MODULES.MONITORING],
  childs: [
    {
      id: 'alerts.alertCenter',
      path: '/alerts/alert-center',
      type: NAV_TYPE_ITEM,
      title: 'Alert Center',
      transKey: 'nav.alerts.alertCenter',
      Icon: ClipboardDocumentIcon,
      modules: [APP_MODULES.INDUSTRIAL, APP_MODULES.MONITORING],
    },
    {
      id: 'alerts.myRules',
      path: '/alerts/my-rules',
      type: NAV_TYPE_ITEM,
      title: 'My Rules',
      transKey: 'nav.alerts.myRules',
      Icon: ClipboardDocumentIcon,
      modules: [APP_MODULES.INDUSTRIAL, APP_MODULES.MONITORING],
    },
    {
      id: 'alerts.settings',
      path: '/alerts/settings',
      type: NAV_TYPE_ITEM,
      title: 'Settings',
      transKey: 'nav.alerts.settings',
      Icon: ClipboardDocumentIcon,
      modules: [APP_MODULES.INDUSTRIAL, APP_MODULES.MONITORING],
    },
  ],
};

const dataSharing = {
  id: 'dataSharing',
  type: NAV_TYPE_ITEM,
  path: '/data-sharing',
  title: 'Data Sharing',
  transKey: 'nav.dashboards.dataSharing',
  Icon: ShareIcon,
  modules: [APP_MODULES.INDUSTRIAL, APP_MODULES.MONITORING, APP_MODULES.MANAGER],
  childs: [
    {
      id: 'dataSharing.export',
      path: '/data-sharing/export',
      type: NAV_TYPE_ITEM,
      title: 'Export Builder',
      transKey: 'nav.dataSharing.export',
      Icon: ClipboardDocumentIcon,
      modules: [APP_MODULES.INDUSTRIAL, APP_MODULES.MONITORING, APP_MODULES.MANAGER],
    },
    {
      id: 'dataSharing.recent',
      path: '/data-sharing/recent',
      type: NAV_TYPE_ITEM,
      title: 'Recent Exports',
      transKey: 'nav.dataSharing.recent',
      Icon: ClipboardDocumentListIcon,
      modules: [APP_MODULES.INDUSTRIAL, APP_MODULES.MONITORING, APP_MODULES.MANAGER],
    },
  ],
};

const help = {
  id: 'help',
  type: NAV_TYPE_ITEM,
  path: '/help-center',
  title: 'Help Center',
  transKey: 'nav.system.help',
  Icon: QuestionMarkCircleIcon,
  modules: ALL_MODULES,
};

// --- Manager-only items ------------------------------------------------

const airPollution = {
  id: 'air-pollution',
  type: NAV_TYPE_ITEM,
  path: '/air-pollution',
  title: 'Air Pollution',
  transKey: 'nav.dashboards.airPollution',
  Icon: CloudIcon,
  modules: [APP_MODULES.MANAGER],
};

const monitoringSection = {
  id: 'monitoring',
  type: NAV_TYPE_ITEM,
  path: '/monitoring',
  title: 'Monitoring',
  transKey: 'nav.dashboards.monitoring',
  Icon: SignalIcon,
  modules: [APP_MODULES.MANAGER],
  childs: [
    {
      id: 'monitoring.analytics',
      path: 'monitoring/analytics',
      type: NAV_TYPE_ITEM,
      title: 'Analytics',
      transKey: 'nav.dashboards.analytics',
      Icon: ChartBarIcon,
      modules: [APP_MODULES.MANAGER],
    },
    {
      id: 'monitoring.daily-profile',
      path: 'reports/daily-profile',
      type: NAV_TYPE_ITEM,
      title: 'Daily Profile',
      transKey: 'nav.dashboards.daily_profile',
      Icon: ChartBarIcon,
      modules: [APP_MODULES.MANAGER],
    },
    {
      id: 'monitoring.seasonal-analysis',
      path: 'reports/seasonal-analysis',
      type: NAV_TYPE_ITEM,
      title: 'Seasonal Analysis',
      transKey: 'nav.dashboards.seasonal_analysis',
      Icon: ChartBarIcon,
      modules: [APP_MODULES.MANAGER],
    },
    {
      id: 'monitoring.correlation-analysis',
      path: 'reports/correlation-analysis',
      type: NAV_TYPE_ITEM,
      title: 'Correlation Analysis',
      transKey: 'nav.dashboards.correlation_analysis',
      Icon: ChartBarIcon,
      modules: [APP_MODULES.MANAGER],
    },
    {
      id: 'monitoring.weather',
      path: 'reports/weather',
      type: NAV_TYPE_ITEM,
      title: 'Weather',
      transKey: 'nav.dashboards.weather',
      Icon: ChartBarIcon,
      modules: [APP_MODULES.MANAGER],
    },
    {
      id: 'monitoring.annual',
      path: 'monitoring/annual',
      type: NAV_TYPE_ITEM,
      title: 'Annual',
      transKey: 'nav.dashboards.annual',
      Icon: ChartBarIcon,
      modules: [APP_MODULES.MANAGER],
    },
  ],
};

const climate = {
  id: 'climate',
  type: NAV_TYPE_ITEM,
  path: '/climate',
  title: 'Climate',
  transKey: 'nav.dashboards.climate',
  Icon: ChartBarIcon,
  modules: [APP_MODULES.MANAGER],
};

const meteorology = {
  id: 'meteorology',
  type: NAV_TYPE_ITEM,
  path: '/meteorology',
  title: 'Meteorology',
  transKey: 'nav.dashboards.meteorology',
  Icon: SunIcon,
  modules: [APP_MODULES.MANAGER],
};

const urbanism = {
  id: 'urbanism',
  type: NAV_TYPE_ITEM,
  path: '/urbanism',
  title: 'Urbanism',
  transKey: 'nav.dashboards.urbanism',
  Icon: BuildingOffice2Icon,
  modules: [APP_MODULES.MANAGER],
};

const health = {
  id: 'health',
  type: NAV_TYPE_ITEM,
  path: '/health',
  title: 'Health',
  transKey: 'nav.dashboards.health',
  Icon: HeartIcon,
  modules: [APP_MODULES.MANAGER],
};

const dailyForecast = {
  id: 'daily-forecast',
  type: NAV_TYPE_ITEM,
  path: '/daily-forecast',
  title: 'Daily Forecast',
  transKey: 'nav.dashboards.dailyForecast',
  Icon: CalendarIcon,
  modules: [APP_MODULES.MANAGER],
};

// --- Admin-only items --------------------------------------------------

const adminDashboard = {
  id: 'admin.dashboard',
  type: NAV_TYPE_ITEM,
  path: '/dashboard',
  title: 'Dashboard',
  transKey: 'nav.admin.dashboard',
  Icon: DashboardsIcon,
  modules: [APP_MODULES.ADMIN],
};

const adminTenants = {
  id: 'admin.tenants',
  type: NAV_TYPE_ITEM,
  path: '/tenants',
  title: 'Tenants',
  transKey: 'nav.admin.tenants',
  Icon: BuildingOffice2Icon,
  modules: [APP_MODULES.ADMIN],
};

const adminUsers = {
  id: 'admin.users',
  type: NAV_TYPE_ITEM,
  path: '/users',
  title: 'Users',
  transKey: 'nav.admin.users',
  Icon: UsersIcon,
  modules: [APP_MODULES.ADMIN],
};

const adminStations = {
  id: 'admin.stations',
  type: NAV_TYPE_ITEM,
  path: '/device-management/stations',
  title: 'Stations',
  transKey: 'nav.admin.stations',
  Icon: SignalIcon,
  modules: [APP_MODULES.ADMIN],
};

const adminGeography = {
  id: 'admin.geography',
  type: NAV_TYPE_ITEM,
  path: '/geography',
  title: 'Geography',
  transKey: 'nav.admin.geography',
  Icon: GlobeAltIcon,
  modules: [APP_MODULES.ADMIN],
  childs: [
    {
      id: 'admin.countries',
      type: NAV_TYPE_ITEM,
      path: '/geography/countries',
      title: 'Countries',
      transKey: 'nav.admin.countries',
      Icon: GlobeAltIcon,
      modules: [APP_MODULES.ADMIN],
    },
    {
      id: 'admin.states',
      type: NAV_TYPE_ITEM,
      path: '/geography/states',
      title: 'States',
      transKey: 'nav.admin.states',
      Icon: MapIcon,
      modules: [APP_MODULES.ADMIN],
    },
    {
      id: 'admin.cities',
      type: NAV_TYPE_ITEM,
      path: '/geography/cities',
      title: 'Cities',
      transKey: 'nav.admin.cities',
      Icon: BuildingLibraryIcon,
      modules: [APP_MODULES.ADMIN],
    },
  ],
};

const adminAuditLog = {
  id: 'admin.auditLog',
  type: NAV_TYPE_ITEM,
  path: '/audit-log',
  title: 'Audit log',
  transKey: 'nav.admin.auditLog',
  Icon: ClipboardDocumentListIcon,
  modules: [APP_MODULES.ADMIN],
};

const adminHelpCenter = {
  id: 'admin.helpCenter',
  type: NAV_TYPE_ITEM,
  path: '/help-center',
  title: 'Help Center',
  transKey: 'nav.admin.helpCenter',
  Icon: QuestionMarkCircleIcon,
  modules: [APP_MODULES.ADMIN],
};

const adminSettings = {
  id: 'admin.settings',
  type: NAV_TYPE_ITEM,
  path: '/settings/configurations',
  title: 'Settings',
  transKey: 'nav.admin.settings',
  Icon: Cog6ToothIcon,
  modules: [APP_MODULES.ADMIN],
  childs: [
    {
      id: 'admin.settings.email',
      type: NAV_TYPE_ITEM,
      path: '/settings/configurations',
      title: 'Email',
      transKey: 'nav.admin.settingsEmail',
      Icon: EnvelopeIcon,
      modules: [APP_MODULES.ADMIN],
    },
    {
      id: 'admin.settings.storage',
      type: NAV_TYPE_ITEM,
      path: '/settings/configurations',
      title: 'File System',
      transKey: 'nav.admin.settingsStorage',
      Icon: CircleStackIcon,
      modules: [APP_MODULES.ADMIN],
    },
    {
      id: 'admin.settings.providers',
      type: NAV_TYPE_ITEM,
      path: '/settings/configurations',
      title: 'Data Providers',
      transKey: 'nav.admin.settingsProviders',
      Icon: GlobeAltIcon,
      modules: [APP_MODULES.ADMIN],
    },
    {
      id: 'admin.settings.system',
      type: NAV_TYPE_ITEM,
      path: '/settings/configurations',
      title: 'System',
      transKey: 'nav.admin.settingsSystem',
      Icon: AdjustmentsHorizontalIcon,
      modules: [APP_MODULES.ADMIN],
    },
  ],
};

// --- Per-module ordering -----------------------------------------------

const ORDER = {
  [APP_MODULES.INDUSTRIAL]: [
    dashboards, 
    alerts, 
    reports,
    devices 
],
  [APP_MODULES.MONITORING]: [
    dashboards,
    climateAnalytics,
    reports,
    devices,
    alerts,
    dataSharing,
    help,
  ],
  [APP_MODULES.MANAGER]: [
    airPollution,
    monitoringSection,
    climate,
    meteorology,
    urbanism,
    health,
    dailyForecast,
    help,
  ],
  [APP_MODULES.ADMIN]: [
    adminDashboard,
    adminTenants,
    adminUsers,
    adminStations,
    adminGeography,
    adminAuditLog,
    adminHelpCenter,
    adminSettings,
  ],
};

// Recursively keep only items the active module has access to.
const filterTree = (items, module) =>
  items
    .filter((item) => !item.modules || item.modules.includes(module))
    .map((item) => {
      if (!item.childs) return item;
      return { ...item, childs: filterTree(item.childs, module) };
    })
    .filter((item) => !item.childs || item.childs.length > 0 || item.path);

export const getNavigationForModule = (module) => {
  const ordered = ORDER[module] || ORDER[APP_MODULES.INDUSTRIAL];
  return filterTree(ordered, module);
};
